import React, { useState, useEffect } from 'react';
import { adminService } from '../../services/admin';
import { KycDocument } from '../../types';
import { Table } from '../../components/ui/Table';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Modal';
import { statusBadge } from '../../components/ui/Button';
import { formatDate } from '../../utils/formatters';
import toast from 'react-hot-toast';
import { Ico } from '../../components/ui/Icon';

const KYCApprovals: React.FC = () => {
  const [kycs, setKycs] = useState<KycDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<any | null>(null);
  const [rejectNote, setRejectNote] = useState('');
  const [modal, setModal] = useState<'view' | null>(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminService.getKycList({ status: 'pending' });
      setKycs(res.data.data || []);
    } catch {} finally { setLoading(false); }
  };

  const viewDetail = async (id: number) => {
    try {
      const res = await adminService.getKycDetail(id);
      setSelected(res.data.kyc);
      setModal('view');
    } catch { toast.error('Failed to load KYC'); }
  };

  const approve = async () => {
    if (!selected) return;
    try {
      await adminService.approveKyc(selected.id);
      toast.success('KYC approved ✓');
      setModal(null);
      load();
    } catch { toast.error('Failed'); }
  };

  const reject = async () => {
    if (!selected || !rejectNote) { toast.error('Rejection note is required'); return; }
    try {
      await adminService.rejectKyc(selected.id, rejectNote);
      toast.success('KYC rejected');
      setModal(null);
      setRejectNote('');
      load();
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="p-4 md:p-6">
      <Table
        loading={loading}
        columns={[
          { key: 'user', label: 'User', render: r => <span className="font-semibold text-gray-50">{(r.user as any)?.username || r.user_id}</span> },
          { key: 'document_type', label: 'Document', render: r => <span className="capitalize font-semibold">{r.document_type?.replace('_', ' ')}</span> },
          { key: 'status', label: 'Status', render: r => statusBadge(r.status) },
          { key: 'submitted_at', label: 'Submitted', render: r => <span className="text-xs text-gray-400">{formatDate(r.submitted_at || '')}</span> },
          { key: 'actions', label: '', render: r => (
            <Button variant="outline" size="sm" onClick={() => viewDetail(r.id)}>Review</Button>
          )},
        ]}
        data={kycs}
        keyExtractor={r => r.id}
        emptyMessage="No pending KYC submissions 🎉"
      />

      <Modal isOpen={modal === 'view'} onClose={() => setModal(null)} title="KYC Review" size="lg">
        {selected && (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-gray-800/40 rounded-xl">
                <p className="text-gray-500 text-xs">User</p>
                <p className="text-gray-50 font-semibold">@{selected.user?.username}</p>
              </div>
              <div className="p-3 bg-gray-800/40 rounded-xl">
                <p className="text-gray-500 text-xs">Document</p>
                <p className="text-gray-50 font-semibold capitalize">{selected.document_type?.replace('_', ' ')}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {selected.front_image_url && (
                <div>
                  <p className="text-gray-500 text-xs mb-1">Front</p>
                  <a href={selected.front_image_url} target="_blank" rel="noopener noreferrer" className="block">
                    <div className="h-24 bg-gray-800/60 rounded-xl border border-gray-700 flex items-center justify-center text-primary text-xs hover:border-primary transition-all">
                      View Front Image →
                    </div>
                  </a>
                </div>
              )}
              {selected.back_image_url && (
                <div>
                  <p className="text-gray-500 text-xs mb-1">Back</p>
                  <a href={selected.back_image_url} target="_blank" rel="noopener noreferrer" className="block">
                    <div className="h-24 bg-gray-800/60 rounded-xl border border-gray-700 flex items-center justify-center text-primary text-xs hover:border-primary">
                      View Back Image →
                    </div>
                  </a>
                </div>
              )}
              {selected.selfie_image_url && (
                <div>
                  <p className="text-gray-500 text-xs mb-1">Selfie</p>
                  <a href={selected.selfie_image_url} target="_blank" rel="noopener noreferrer" className="block">
                    <div className="h-24 bg-gray-800/60 rounded-xl border border-gray-700 flex items-center justify-center text-primary text-xs hover:border-primary">
                      View Selfie →
                    </div>
                  </a>
                </div>
              )}
            </div>

            <Input label="Rejection Note (required if rejecting)" placeholder="Reason for rejection..." value={rejectNote} onChange={e => setRejectNote(e.target.value)} />

            <div className="grid grid-cols-2 gap-3">
              <Button variant="danger" fullWidth icon={<Ico name="close" />} onClick={reject}>Reject</Button>
              <Button fullWidth icon={<Ico name="check" />} onClick={approve}>Approve ✓</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default KYCApprovals;
